import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, MapPin as MapPinIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { GalleryImage, getGalleryImageUrl, getYouTubeEmbedUrl } from '../../services/galleryService';
import { appConfig } from '../../config/appConfig';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  country: string;
  images: GalleryImage[];
}

const ImageModal: React.FC<ImageModalProps> = ({ isOpen, onClose, country, images }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [randomLogo, setRandomLogo] = useState<string>('');
  
  const currentImage = images[currentIndex];
  const imagePath = currentImage?.filename ? getGalleryImageUrl(currentImage.filename) : '';
  const hasMultipleImages = images.length > 1;
  const hasImage = !!imagePath;
  const hasVideo = !!currentImage?.youtubeUrl;
  
  // Select random logo when there's no image but there is a video
  useEffect(() => {
    if (!hasImage && hasVideo) {
      const randomIndex = Math.floor(Math.random() * appConfig.fallbackLogos.length);
      setRandomLogo(appConfig.fallbackLogos[randomIndex]);
    }
  }, [hasImage, hasVideo, currentIndex]);

  useEffect(() => {
    setImageLoaded(false);
    if (isOpen && imagePath) {
      // Preload image
      const img = new Image();
      img.src = imagePath;
      img.onload = () => setImageLoaded(true);
    }
  }, [isOpen, imagePath]);

  // Reset to first image when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  const handleExploreTours = () => {
    onClose();
    navigate('/tours');
  };

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (hasMultipleImages) {
        if (e.key === 'ArrowLeft') handlePrevious();
        if (e.key === 'ArrowRight') handleNext();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, hasMultipleImages, handlePrevious, handleNext]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal Content */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: 'spring', duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white dark:hover:bg-slate-800 transition-all group"
                aria-label="Close modal"
              >
                <X className="w-6 h-6 text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white" />
              </button>

              {/* Image Container */}
              <div className="relative w-full aspect-video bg-slate-100 dark:bg-slate-800">
                {hasImage ? (
                  <>
                    {!imageLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    <motion.img
                      key={currentIndex}
                      src={imagePath}
                      alt={`${country} ${currentImage?.year}`}
                      className={`w-full h-full object-cover transition-opacity duration-500 ${
                        imageLoaded ? 'opacity-100' : 'opacity-0'
                      }`}
                      initial={{ scale: 1.1 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.6 }}
                    />
                  </>
                ) : (
                  <>
                    {hasVideo && randomLogo ? (
                      <motion.img
                        key={randomLogo}
                        src={randomLogo}
                        alt={`${country} logo`}
                        className="w-full h-full object-contain p-8 gradient-slate-panel"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center gradient-slate-panel">
                        <div className="text-center">
                          <MapPinIcon className="w-16 h-16 mx-auto mb-4 text-slate-400 dark:text-slate-500" />
                          <p className="text-slate-600 dark:text-slate-400 font-medium">No image available</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Navigation Arrows for Multiple Images */}
                {hasMultipleImages && (
                  <>
                    <button
                      onClick={handlePrevious}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full transition-all group z-10"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                    <button
                      onClick={handleNext}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full transition-all group z-10"
                      aria-label="Next image"
                    >
                      <ChevronRight className="w-6 h-6 text-white" />
                    </button>
                  </>
                )}
                
                {/* Image Overlay with Gradient */}
                <div className="absolute bottom-0 left-0 right-0 gradient-overlay-top p-6">
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <h2 className="text-3xl font-bold text-white mb-2 font-serif">
                      {country}
                    </h2>
                    <div className="flex items-center gap-4 text-white/90">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm font-medium">{currentImage?.year}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">Gallery Collection</span>
                      </div>
                      {hasMultipleImages && (
                        <span className="text-sm font-medium">
                          {currentIndex + 1} / {images.length}
                        </span>
                      )}
                    </div>
                  </motion.div>
                </div>

                {/* Carousel Dots */}
                {hasMultipleImages && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentIndex
                            ? 'bg-white w-8'
                            : 'bg-white/50 hover:bg-white/75'
                        }`}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Additional Info Section */}
              <div className="p-6 border-t border-slate-200 dark:border-slate-700">
                {/* Description */}
                {currentImage?.description && (
                  <div className="mb-4">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {currentImage.description}
                    </p>
                  </div>
                )}

                {/* YouTube Video Section */}
                {currentImage?.youtubeUrl && getYouTubeEmbedUrl(currentImage.youtubeUrl) && (
                  <div className="mb-4">
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800">
                      <iframe
                        src={getYouTubeEmbedUrl(currentImage.youtubeUrl)}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                    <p className="mt-2 text-sm text-center text-slate-600 dark:text-slate-400">
                      Video from YouTube
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                      Travel Memories
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Experience the beauty and culture of {country}
                    </p>
                  </div>
                  <button
                    onClick={handleExploreTours}
                    className="btn-cta-amber !px-6 !py-2 !rounded-full"
                  >
                    Explore Tours
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ImageModal;

